import { Alert, DatePicker, Form, Select } from 'antd'
import moment from 'moment'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { workScheduleApi } from '../../../../../apis'
import { ActionEnum, AlertMessageEnum } from '../../../../../common'
import {
  ManageModalComponent,
  ManageModalFooterComponent,
} from '../../../../../components'
import { translate } from '../../../../../languages'
import { EmployeeEntity, ShiftEntity } from '../../../../../models'
import { RootState } from '../../../../../stores'
import {
  loadEmployees,
  loadProfile,
  loadShifts,
  loadStations,
  loadWorkSchedules,
  setAction,
  setActionProcessing,
  setAlert,
  setSelectedWorkSchedule,
  setWorkSchedules,
} from '../../../../../stores/actions'

const StyledFormItem = styled(Form.Item)`
  margin-top: 5px;
`

const StyledLabel = styled.span`
  font-weight: bold;
`

type InputWorkSchedule = {
  employeeId: number
  shiftId: number
  startDate: Date
  endDate: Date
}

export const ManageWorkScheduleModalComponent: React.FC = () => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const { language } = useSelector((state: RootState) => state.language)
  const { management } = useSelector((state: RootState) => state.common)
  const { workSchedules, selectedWorkSchedule } = useSelector(
    (state: RootState) => state.workSchedule
  )
  const { stations, isStationsLoaded } = useSelector(
    (state: RootState) => state.station
  )
  const { employees, isEmployeesLoaded } = useSelector(
    (state: RootState) => state.employee
  )
  const { shifts, isShiftsLoaded } = useSelector(
    (state: RootState) => state.shift
  )

  React.useEffect(() => {
    dispatch(loadStations())
    dispatch(loadEmployees())
    dispatch(loadShifts())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [availableEmployees, setAvailableEmployees] = React.useState<
    EmployeeEntity[]
  >([])

  const [availableShifts, setAvailableShifts] = React.useState<ShiftEntity[]>(
    shifts.filter(
      (shift) =>
        selectedWorkSchedule &&
        shift.station.id === selectedWorkSchedule.employee.station.id
    )
  )

  const readOnlyMode = React.useMemo(
    () => [ActionEnum.VIEW, ActionEnum.DELETE].includes(management.action),
    [management]
  )

  const onFinish = async (value: InputWorkSchedule) => {
    dispatch(setAlert())
    dispatch(setActionProcessing(true))
    switch (management.action) {
      case ActionEnum.VIEW:
        dispatch(setAction(ActionEnum.UPDATE))
        break
      case ActionEnum.UPDATE:
        if (selectedWorkSchedule) {
          try {
            await workScheduleApi.updateWorkSchedule(selectedWorkSchedule.id, {
              ...value,
              startDate: new Date(
                value.startDate || selectedWorkSchedule.startDate
              ).getTime(),
              endDate: new Date(
                value.endDate || selectedWorkSchedule.endDate
              ).getTime(),
            })
            dispatch(loadWorkSchedules())
            dispatch(setSelectedWorkSchedule())
            dispatch(
              setAlert({
                key: 'workSchedule.update.success',
                type: AlertMessageEnum.SUCCESS,
              })
            )
          } catch (e) {
            if (e && e.response && e.response.status === 401) {
              dispatch(loadProfile())
              await workScheduleApi.updateWorkSchedule(
                selectedWorkSchedule.id,
                {
                  ...value,
                  startDate: new Date(
                    value.startDate || selectedWorkSchedule.startDate
                  ).getTime(),
                  endDate: new Date(
                    value.endDate || selectedWorkSchedule.endDate
                  ).getTime(),
                }
              )
              dispatch(loadWorkSchedules())
              dispatch(setSelectedWorkSchedule())
              dispatch(
                setAlert({
                  key: 'workSchedule.update.success',
                  type: AlertMessageEnum.SUCCESS,
                })
              )
            } else if (e && e.response && e.response.status === 409) {
              dispatch(
                setAlert({
                  key: 'workSchedule.create.fail.duplicate.time',
                  type: AlertMessageEnum.ERROR,
                })
              )
            } else {
              dispatch(
                setAlert({
                  key: 'workSchedule.update.fail.internal',
                  type: AlertMessageEnum.ERROR,
                })
              )
            }
          }
        }
        break
      case ActionEnum.CREATE:
        try {
          await workScheduleApi.createWorkSchedule({
            ...value,
            startDate: new Date(value.startDate).getTime(),
            endDate: new Date(value.endDate).getTime(),
          })
          dispatch(loadWorkSchedules())
          dispatch(
            setAlert({
              key: 'workSchedule.create.success',
              type: AlertMessageEnum.SUCCESS,
            })
          )
        } catch (e) {
          if (e && e.response && e.response.status === 401) {
            dispatch(loadProfile())
            await workScheduleApi.createWorkSchedule({
              ...value,
              startDate: new Date(value.startDate).getTime(),
              endDate: new Date(value.endDate).getTime(),
            })
            dispatch(loadWorkSchedules())
            dispatch(
              setAlert({
                key: 'workSchedule.create.success',
                type: AlertMessageEnum.SUCCESS,
              })
            )
          } else if (e && e.response && e.response.status === 409) {
            dispatch(
              setAlert({
                key: 'workSchedule.create.fail.duplicate.time',
                type: AlertMessageEnum.ERROR,
              })
            )
          } else {
            dispatch(
              setAlert({
                key: 'workSchedule.create.fail.internal',
                type: AlertMessageEnum.ERROR,
              })
            )
          }
        }
        break
      case ActionEnum.DELETE:
        if (selectedWorkSchedule) {
          try {
            await workScheduleApi.deleteWorkSchedule(selectedWorkSchedule.id)
            dispatch(
              setWorkSchedules(
                workSchedules.filter(
                  (item) => item.id !== selectedWorkSchedule.id
                )
              )
            )
            dispatch(loadWorkSchedules())
            dispatch(setSelectedWorkSchedule())
            dispatch(
              setAlert({
                key: 'workSchedule.delete.success',
                type: AlertMessageEnum.SUCCESS,
              })
            )
          } catch (e) {
            if (e && e.response && e.response.status === 401) {
              dispatch(loadProfile())
              await workScheduleApi.deleteWorkSchedule(selectedWorkSchedule.id)
              dispatch(
                setWorkSchedules(
                  workSchedules.filter(
                    (item) => item.id !== selectedWorkSchedule.id
                  )
                )
              )
              dispatch(loadWorkSchedules())
              dispatch(setSelectedWorkSchedule())
              dispatch(
                setAlert({
                  key: 'workSchedule.delete.success',
                  type: AlertMessageEnum.SUCCESS,
                })
              )
            } else {
              dispatch(
                setAlert({
                  key:
                    e.response && e.response.status && e.response.status === 400
                      ? 'common.delete.fail.fkConstraint'
                      : 'workSchedule.delete.fail.internal',
                  type: AlertMessageEnum.ERROR,
                })
              )
            }
          }
        }
        break
      default:
        break
    }
    dispatch(setActionProcessing(false))
  }

  const canUpdateStartDate =
    (selectedWorkSchedule &&
      new Date(selectedWorkSchedule.startDate).getDate() >
        new Date().getDate()) ||
    management.action === ActionEnum.CREATE

  const canUpdateEndDate =
    (selectedWorkSchedule &&
      new Date(selectedWorkSchedule.endDate).getDate() >
        new Date().getDate()) ||
    management.action === ActionEnum.CREATE

  const body = (
    <Form onFinish={onFinish} form={form}>
      {management.action === ActionEnum.DELETE &&
        (!management.alertMessage ||
          management.alertMessage.type !== AlertMessageEnum.SUCCESS) &&
        !canUpdateEndDate && (
          <Alert
            showIcon
            closable
            type="warning"
            message={translate('common.alert.warning', language)}
            description={translate(
              'workSchedule.warning.canNotDelete',
              language
            )}
            style={{ marginBottom: '10px' }}
          />
        )}
      {(!management.alertMessage ||
        management.alertMessage.type !== AlertMessageEnum.SUCCESS) && (
        <>
          {management.action !== ActionEnum.CREATE ? (
            <>
              <StyledFormItem
                label={
                  <StyledLabel>
                    {translate('workSchedule.id', language)}
                  </StyledLabel>
                }
              >
                {selectedWorkSchedule?.id}
              </StyledFormItem>
              <StyledFormItem
                label={
                  <StyledLabel>
                    {translate('workSchedule.employee', language)}
                  </StyledLabel>
                }
              >
                {selectedWorkSchedule?.employee.name}
              </StyledFormItem>
              <StyledFormItem
                label={
                  <StyledLabel>
                    {translate('workSchedule.station', language)}
                  </StyledLabel>
                }
              >
                {selectedWorkSchedule?.employee.station.name}
              </StyledFormItem>
              <StyledFormItem
                label={
                  <StyledLabel>
                    {translate('workSchedule.address', language)}
                  </StyledLabel>
                }
              >
                {selectedWorkSchedule?.employee.station.address}
              </StyledFormItem>
              <StyledFormItem
                label={
                  <StyledLabel>
                    {translate('workSchedule.owner', language)}
                  </StyledLabel>
                }
              >
                {selectedWorkSchedule?.employee.station.owner.name}
              </StyledFormItem>
            </>
          ) : (
            <>
              <StyledFormItem
                name="stationId"
                label={
                  <StyledLabel>
                    {translate('workSchedule.station', language)}
                  </StyledLabel>
                }
                initialValue={selectedWorkSchedule?.employee.station.id}
                rules={[
                  {
                    required: true,
                    message: translate(
                      'workSchedule.required.station',
                      language
                    ),
                  },
                ]}
              >
                <Select
                  placeholder={translate('workSchedule.station', language)}
                  style={{ width: '100%' }}
                  loading={!isStationsLoaded}
                  disabled={!isStationsLoaded}
                  onChange={(e) => {
                    form.setFieldsValue({
                      employeeId: undefined,
                      shiftId: undefined,
                    })
                    setAvailableEmployees(
                      employees.filter((employee) => employee.station.id === e)
                    )
                    setAvailableShifts(
                      shifts.filter((shift) => shift.station.id === e)
                    )
                  }}
                >
                  {stations.map((item) => (
                    <Select.Option key={item.id} value={item.id}>
                      {`${item.name} ( ${item.address} )`}
                    </Select.Option>
                  ))}
                </Select>
              </StyledFormItem>
              <StyledFormItem
                name="employeeId"
                label={
                  <StyledLabel>
                    {translate('workSchedule.employee', language)}
                  </StyledLabel>
                }
                initialValue={selectedWorkSchedule?.employee.id}
                rules={[
                  {
                    required: true,
                    message: translate(
                      'workSchedule.required.employee',
                      language
                    ),
                  },
                ]}
              >
                <Select
                  placeholder={translate('workSchedule.employee', language)}
                  style={{ width: '100%' }}
                  loading={!isStationsLoaded || !isEmployeesLoaded}
                  disabled={!isStationsLoaded || !isEmployeesLoaded}
                >
                  {availableEmployees.map((item) => (
                    <Select.Option key={item.id} value={item.id}>
                      {`${item.name} ( ${item.phone} )`}
                    </Select.Option>
                  ))}
                </Select>
              </StyledFormItem>
            </>
          )}
          {canUpdateStartDate && !readOnlyMode ? (
            <StyledFormItem
              name="shiftId"
              label={
                <StyledLabel>
                  {translate('workSchedule.shift', language)}
                </StyledLabel>
              }
              initialValue={selectedWorkSchedule?.shift.id}
              rules={[
                {
                  required: true,
                  message: translate('workSchedule.required.shift', language),
                },
              ]}
            >
              <Select
                placeholder={translate('workSchedule.shift', language)}
                style={{ width: '100%' }}
                loading={!isShiftsLoaded}
                disabled={!isShiftsLoaded}
              >
                {availableShifts.map((item) => (
                  <Select.Option key={item.id} value={item.id}>
                    {`${item.name} ( ${item.startTime}-${item.endTime} )`}
                  </Select.Option>
                ))}
              </Select>
            </StyledFormItem>
          ) : (
            <>
              <StyledFormItem
                label={
                  <StyledLabel>
                    {translate('workSchedule.shift', language)}
                  </StyledLabel>
                }
              >
                {selectedWorkSchedule?.shift.name}
              </StyledFormItem>
              <StyledFormItem
                label={
                  <StyledLabel>
                    {translate('shift.startTime', language)}
                  </StyledLabel>
                }
              >
                {selectedWorkSchedule?.shift.startTime}
              </StyledFormItem>
              <StyledFormItem
                label={
                  <StyledLabel>
                    {translate('shift.endTime', language)}
                  </StyledLabel>
                }
              >
                {selectedWorkSchedule?.shift.endTime}
              </StyledFormItem>
            </>
          )}
          {canUpdateStartDate && !readOnlyMode ? (
            <StyledFormItem
              label={
                <StyledLabel>
                  {translate('workSchedule.startDate', language)}
                </StyledLabel>
              }
              name="startDate"
              initialValue={moment(
                selectedWorkSchedule?.startDate ||
                  new Date(new Date().setDate(new Date().getDate() + 1))
              )}
              rules={[
                {
                  required: true,
                  message: '',
                },
                () => ({
                  validator(_, value) {
                    if (!value) {
                      return Promise.reject(
                        new Error(
                          translate('workSchedule.required.startDate', language)
                        )
                      )
                    } else if (
                      value <= new Date() ||
                      value > form.getFieldValue('endDate')
                    ) {
                      return Promise.reject(
                        new Error(
                          translate('workSchedule.invalid.startDate', language)
                        )
                      )
                    } else {
                      return Promise.resolve()
                    }
                  },
                }),
              ]}
            >
              <DatePicker
                placeholder={translate('workSchedule.startDate', language)}
                format="YYYY-MM-DD"
              />
            </StyledFormItem>
          ) : (
            <StyledFormItem
              label={
                <StyledLabel>
                  {translate('workSchedule.startDate', language)}
                </StyledLabel>
              }
            >
              {new Date(
                selectedWorkSchedule?.startDate || new Date().getTime()
              ).toLocaleDateString()}
            </StyledFormItem>
          )}
          {canUpdateEndDate && !readOnlyMode ? (
            <StyledFormItem
              label={
                <StyledLabel>
                  {translate('workSchedule.endDate', language)}
                </StyledLabel>
              }
              name="endDate"
              initialValue={moment(
                selectedWorkSchedule?.endDate ||
                  new Date(new Date().setDate(new Date().getDate() + 1))
              )}
              rules={[
                {
                  required: true,
                  message: '',
                },
                () => ({
                  validator(_, value) {
                    const startDate = selectedWorkSchedule
                      ? selectedWorkSchedule.startDate
                      : form.getFieldValue('endDate')
                    if (!value) {
                      return Promise.reject(
                        new Error(
                          translate('workSchedule.required.endDate', language)
                        )
                      )
                    } else if (
                      value < startDate ||
                      value <= new Date().getTime()
                    ) {
                      return Promise.reject(
                        new Error(
                          translate('workSchedule.invalid.endDate', language)
                        )
                      )
                    } else {
                      return Promise.resolve()
                    }
                  },
                }),
              ]}
            >
              <DatePicker
                placeholder={translate('workSchedule.endDate', language)}
                format="YYYY-MM-DD"
              />
            </StyledFormItem>
          ) : (
            <StyledFormItem
              label={
                <StyledLabel>
                  {translate('workSchedule.endDate', language)}
                </StyledLabel>
              }
            >
              {new Date(
                selectedWorkSchedule?.endDate || new Date().getTime()
              ).toLocaleDateString()}
            </StyledFormItem>
          )}
        </>
      )}
      <ManageModalFooterComponent
        hasDefaultButtons={
          !readOnlyMode ||
          (management.action === ActionEnum.DELETE && canUpdateStartDate) ||
          (management.action === ActionEnum.VIEW && canUpdateEndDate)
        }
        onDeleteConfirm={() => form.submit()}
      />
    </Form>
  )

  return <ManageModalComponent body={body} />
}
